import React, { useEffect, useRef, useState } from 'react';
import HtmlParser from 'html-react-parser';
import styled from 'styled-components/macro';

import { zIndex } from '@mirinae/react-ui';

import { ReactComponent as IconZoom } from '@mirinae/wikinae/components/assets/icon-zoom-30.svg';
import IconCancel from '@mirinae/wikinae/components/assets/icon-cancel-12.svg';
import { wikinaePalette, wikinaeSize } from '@mirinae/wikinae/modules/defines/styles';

const SearchBox = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    gap: 0 10px;
    height: ${props => (props.theme === 'header' ? '55px' : '50px')};
    padding: 15px 46px 15px 20px;
    border-radius: 5px;
    background-color: ${wikinaePalette.desat[4]};

    & > svg {
        width: 25px;
        height: 25px;
        pointer-events: none;

        path {
            fill: ${wikinaePalette.desat[2]};
            stroke: ${wikinaePalette.desat[2]};
        }
    }
    input {
        width: 249px;
        height: 20px;
        border: 0;
        background-color: transparent;
        font-size: 16px;
        color: ${wikinaePalette.primary.black};
        ::placeholder {
            color: ${wikinaePalette.desat[2]};
        }
    }

    ${props => (props.theme !== 'header' && `
        width: 100%;
        input {
            width: 100%;
        }
        @media only screen and (max-width: ${wikinaeSize.breakpoint.sm}) {
            width: 100%;
        }
    `)}
`;
const ClearButton = styled.button`
    position: absolute;
    right: 18px;
    top: 50%;
    width: 20px;
    height: 20px;
    transform: translateY(-50%);
    background: url(${IconCancel}) no-repeat 50%;
    font-size: 0;
`;
const SearchLayer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 40px 0;
    ${props => (props.theme === 'layer' ? `
        padding: 30px 10px;
    ` : `
        position: absolute;
        top: 100%;
        left: ${props.theme === 'header' ? '44px' : 0};
        right: 0;
        z-index: ${props.theme === 'header' ? zIndex.header + 1 : 1};
        margin-top: 5px;
        padding: 20px;
        border: 1px solid ${wikinaePalette.desat[3]};
        border-radius: 5px;
        background-color: ${wikinaePalette.desat[4]};
    `)}
`;
const SearchItem = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 25px 0;
`;
const SearchTitle = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: ${wikinaePalette.desat[1]};
`;
const SearchList = styled.ul`
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    max-height: ${props => (props.theme === 'row' ? '196px' : '88px')};
    overflow: auto;
    padding: 0 ${props => (props.theme === 'row' ? '15px' : '5px')};
    li {
        width: ${props => (props.theme === 'row' ? '100%' : 'calc(50% - 10px)')};
        height: 16px;
        line-height: 100%;
        font-size: 16px;
        font-weight: 600;
        color: ${wikinaePalette.primary.black};
        cursor: pointer;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        cursor: pointer;
        button {
            line-height: inherit;
            font-size: inherit;
            font-weight: inherit;
            color: inherit;
        }
        b {
            color: ${wikinaePalette.main.main};
            font-weight: inherit;
        }
    }
`;
const TextButton = styled.button`
    position: absolute;
    right: 0;
    top: 2px;
    font-size: 12px;
    font-weight: 600;
    color: ${wikinaePalette.desat[2]};
`;

/*
 * theme: header(GNB toggle layer) | layer(mobile size header layer) | content(sub page)
 */
const SearchBlock = ({ theme, query, searchTerms = [], userTerms = [], popular = [], onChange, onClick, onDelete }) => {
    const [search, setSearch] = useState('');
    const [displayLayer, setDisplayLayer] = useState(theme === 'layer');

    const $searchForm = useRef();
    const $layer = useRef();

    const closeLayer = (e) => {
        if (theme === 'layer' || e.target === $searchForm.current || e.target === $layer.current || e.target.nodeName === 'INPUT' || e.target.id === 'btnDeleteAll') return;
        setDisplayLayer(false);
    };

    useEffect(() => {
        setSearch('');
        setDisplayLayer(theme === 'layer');

        document.body.addEventListener('click', closeLayer);
        return () => {
            document.body.removeEventListener('click', closeLayer);
        };
    }, []);

    useEffect(() => {
        onChange(search);
    }, [search]);

    useEffect(() => {
        setSearch(query);
    }, [query]);

    const handleClickArea = (e) => {
        if (theme === 'layer') return;
        if (e.currentTarget === $searchForm.current) {
            setDisplayLayer(true);
        }
    };

    return (
        <>
            <SearchBox ref={$searchForm} onClick={handleClickArea} theme={theme}>
                <IconZoom />
                <input
                    type="text"
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={e => {
                        const key = e.key || e.keyCode;
                        if (key === 'Enter' || key === 13) {
                            onClick(search);
                        }
                    }}
                />
                {search.length > 0 && (
                    <ClearButton
                        type="button"
                        onClick={(e) => {
                            e.preventDefault();
                            setSearch('');
                        }}>
                        Clear input
                    </ClearButton>
                )}
            </SearchBox>

            {displayLayer && theme.match(/header|layer/) && ((search.length === 0 ? popular.length > 0 : searchTerms.length > 0) || userTerms.length > 0) && (
                <SearchLayer theme={theme} ref={$layer}>
                    {userTerms.length > 0 && (
                        <SearchItem>
                            <TextButton type="button" onClick={onDelete} id="btnDeleteAll">Delete All</TextButton>
                            <SearchTitle>
                                Recent Search History
                            </SearchTitle>
                            <SearchList>
                                {userTerms.map((s, i) => (
                                    <li key={i}><button type="button" onClick={() => onClick(s)}>{s}</button></li>
                                ))}
                            </SearchList>
                        </SearchItem>
                    )}
                    {search.length === 0 ? popular.length > 0 && (
                        <SearchItem>
                            <SearchTitle>
                                What people are searching for...
                            </SearchTitle>
                            <SearchList>
                                {popular.map((s, i) => (
                                    <li key={i}><button type="button" onClick={() => onClick(s.text)}>{s.text}</button></li>
                                ))}
                            </SearchList>
                        </SearchItem>
                    ) : (
                        <SearchItem>
                            <SearchTitle>
                                Are you looking for..
                            </SearchTitle>
                            <SearchList theme="row">
                                {searchTerms.map((s, i) => {
                                    const { text } = s;
                                    const t = text.split('');
                                    const pre = t.slice(0, search.length);
                                    const post = t.slice(search.length, t.length);
                                    const markup = `<b>${pre.join('')}</b>${post.join('')}`;
                                    return (
                                        <li key={i}><button type="button" onClick={() => onClick(pre.join() + post.join())}>{HtmlParser(markup)}</button></li>
                                    );
                                })}
                            </SearchList>
                        </SearchItem>
                    )}
                </SearchLayer>
            )}
        </>
    );
};

export default SearchBlock;